import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, finalize, map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { BreadcrumbService, createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import { Roll } from '@app/models/knitting/roll';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { Asset } from '../../../../models/asset/asset';
import { AssetProxyService } from '../../../../proxies/asset/asset.proxy.service';

@Component({
  selector: 'app-weight-station',
  templateUrl: './weight-station.component.html',
  styleUrl: './weight-station.component.scss',
})
export class WeightStationComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private assetProxyService = inject(AssetProxyService);
  private rollProxyService = inject(RollProxyService);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private breadcrumbService = inject(BreadcrumbService);

  weightStation: Asset | null = null;
  isLoading = signal(0);
  rolls = signal<Roll[]>([]);
  filteredRolls = signal<Roll[]>([]);
  rollQualities: EnumValues = {};
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'weight-station-filters', config: this.filterConfig });
  showFilters = signal(false);

  private filtersPanelInitialized = false;

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.tryInitializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.filterRolls(filters);
    });

    this.rollQualities = this.enumService.getEnum('RollQuality', 'productionEnums')?.values ?? {};
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        map((params) => params.get('id')),
        distinctUntilChanged(),
      )
      .subscribe((assetId) => {
        if (assetId) {
          this.getAssetById(assetId);
        } else {
          this.router.navigate(['stations']);
        }
      });
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    if (!this.rollQualities || Object.keys(this.rollQualities).length === 0) {
      this.rollQualities = this.enumService.getEnum('RollQuality', 'productionEnums')?.values ?? {};
    }

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      showResetButton: false,
      filters: [
        {
          type: 'text',
          formControlName: 'assetId',
          label: translate('Greige_Code', 'Greige Code'),
          placeholder: translate('Enter_greige_code', 'Enter greige code'),
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  getLastWeightTime(roll: Roll): string {
    const lastStamp = _.maxBy(roll.stationTimeStamps, (s) => new Date(s.timeStamp!).getTime());

    if (!lastStamp?.timeStamp) return 'N/A';

    return new Date(lastStamp.timeStamp).toLocaleString();
  }

  getAssetById(assetId: string) {
    this.startLoading();
    this.assetProxyService.getByAssetId(assetId).subscribe({
      next: (assetResult) => {
        this.weightStation = assetResult.data;
        this.breadcrumbService.setCurrent(this.weightStation?.assetId);
        this.getRollsByAssetId(assetId);
        this.stopLoading();
      },
      error: (error) => {
        console.error(error);
        this.router.navigate(['stations']);
        this.stopLoading();
      },
    });
  }

  getRollsByAssetId(assetId: string): void {
    this.startLoading();
    this.rollProxyService
      .getRollsByAssetId(assetId)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (!_.isNil(response.data)) {
            this.rolls.set(response.data);
            this.filterRolls(this.filterState.applied());
          }
        },
        error: (error) => {
          console.error('Error fetching rolls:', error);
          this.rolls.set([]);
          this.filteredRolls.set([]);
        },
      });
  }

  private filterRolls(filters: DynamicFilterValues): void {
    const assetId = (filters['assetId'] as string)?.toLowerCase() || '';

    let filtered = this.rolls();

    if (assetId) {
      filtered = filtered.filter((roll) => roll.assetId?.toLowerCase().includes(assetId));
    }

    this.filteredRolls.set(filtered);
  }

  redirectToWeightStationDetails(id?: string): void {
    const queryParams = id ? { id } : {};
    this.router.navigate(['/weight-station-detail'], { queryParams });
  }
}
